import React from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomSelect, OptionType } from 'components/form';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';
import { PersonalityAlignment } from 'components/job-criteria/types';

type PersonalityAlignmentFormData = {
  personalityAlignments: PersonalityAlignment[];
};

const personalityAlignmentSchema = yup.object().shape({
  personalityAlignments: yup.array().of(
    yup.object().shape({
      category: yup.string().required('Category is required'),
      description: yup.string().required('Description is required'),
      style: yup.string().required('Style is required'),
      implication: yup.string().required('Implication is required'),
      mostPreferred: yup.string().required('Most Preferred is required'),
      nextPreferred: yup.string().optional().default(''),
      leastPreferred: yup.string().optional().default(''),
      mostPreferredDescription: yup.string().optional().default(''),
    })
  ).required('At least one personality alignment is required').min(1, 'At least one personality alignment is required'),
});

interface PersonalityAlignmentsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: PersonalityAlignmentFormData;
  onSave: (data: PersonalityAlignmentFormData) => void;
  isSubmitting?: boolean;
}

export const PersonalityAlignmentsEditModal: React.FC<PersonalityAlignmentsEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting,
}) => {
  const defaultValues: PersonalityAlignmentFormData = {
    personalityAlignments: currentData?.personalityAlignments.map((alignment) => ({
      category: alignment.category,
      description: alignment.description,
      style: alignment.style,
      implication: alignment.implication,
      mostPreferred: alignment.mostPreferred || '',
      nextPreferred: alignment.nextPreferred || '',
      leastPreferred: alignment.leastPreferred || '',
      mostPreferredDescription: alignment.mostPreferredDescription || '',
    })) || [],
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PersonalityAlignmentFormData>({
    defaultValues,
    resolver: yupResolver(personalityAlignmentSchema),
  });

  const { fields } = useFieldArray({
    control,
    name: 'personalityAlignments',
  });

  const onSubmit = (data: PersonalityAlignmentFormData) => {
    // The only stored option description is for the originally most-preferred
    // option. When the user re-ranks and picks a different most-preferred
    // option, resolve the description for the newly selected value so the
    // preview never shows a description that belongs to a different option.
    const syncedData: PersonalityAlignmentFormData = {
      personalityAlignments: data.personalityAlignments.map((alignment, index) => {
        const original = currentData?.personalityAlignments?.[index];
        const descriptionByValue: Record<string, string> =
          original?.mostPreferred
            ? { [original.mostPreferred]: original.mostPreferredDescription || '' }
            : {};
        return {
          ...alignment,
          mostPreferredDescription: descriptionByValue[alignment.mostPreferred] ?? '',
        };
      }),
    };
    onSave(syncedData);
  };

  // Each trait's dropdowns share one pool of statements (its most/next/least
  // preferred values). We build that pool from the backend data and merge in the
  // current live selections so every distinct, non-empty statement is always
  // available to choose from across all three dropdowns, with no duplicates and
  // without ever dropping the option the user currently has selected.
  const getRowOptions = (
    alignment: PersonalityAlignment | undefined,
    liveValues?: Partial<Pick<PersonalityAlignment, 'mostPreferred' | 'nextPreferred' | 'leastPreferred'>>
  ): OptionType[] => {
    const values = [
      alignment?.mostPreferred,
      alignment?.nextPreferred,
      alignment?.leastPreferred,
      liveValues?.mostPreferred,
      liveValues?.nextPreferred,
      liveValues?.leastPreferred,
    ].filter((v): v is string => Boolean(v && v.trim()));
    return Array.from(new Set(values)).map((v) => ({ value: v, label: v }));
  };

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Personality Qualification"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[92%] mx-auto my-[10px]">
        <p className="mb-[20px] pb-3 border-b border-[#D0D5DD]">
          You can select any of the Personality Qualifications that best resonate with you.
        </p>

        <div className="mb-5">
          <h1 className="font-semibold text-lg mb-2.5">Personality Qualification</h1>

          {fields.map((field, index) => {
            const rowOptions = getRowOptions(
              currentData?.personalityAlignments?.[index],
              watch(`personalityAlignments.${index}`)
            );
            const getOptionByValue = (value: string): OptionType =>
              rowOptions.find((opt) => opt.value === value) || { value: '', label: 'Select' };

            return (
              <div key={field.id} className="mb-5">
                <h3 className="font-medium mb-2.5">{field.category}</h3>
                <p className="font-[Gelion] text-[#1a3c63] mb-1.5">{field.description}</p>
                <p className="text-[#667085] italic mb-2.5">Change to what resonates most:</p>

                <div className="flex gap-2.5 mt-2.5">
                  <Controller
                    name={`personalityAlignments.${index}.mostPreferred`}
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        label="Most Preferred"
                        placeholder="Select"
                        options={rowOptions}
                        value={getOptionByValue(field.value)}
                        onChange={(option) => field.onChange(option?.value || '')}
                        validatorMessage={errors.personalityAlignments?.[index]?.mostPreferred?.message || ''}
                        name={`personalityAlignments.${index}.mostPreferred`}
                      />
                    )}
                  />
                  <Controller
                    name={`personalityAlignments.${index}.nextPreferred`}
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        label="Next Preferred"
                        placeholder="Select"
                        options={rowOptions}
                        value={getOptionByValue(field.value)}
                        onChange={(option) => field.onChange(option?.value || '')}
                        validatorMessage={errors.personalityAlignments?.[index]?.nextPreferred?.message || ''}
                        name={`personalityAlignments.${index}.nextPreferred`}
                      />
                    )}
                  />
                  <Controller
                    name={`personalityAlignments.${index}.leastPreferred`}
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        label="Least Preferred"
                        placeholder="Select"
                        options={rowOptions}
                        value={getOptionByValue(field.value)}
                        onChange={(option) => field.onChange(option?.value || '')}
                        validatorMessage={errors.personalityAlignments?.[index]?.leastPreferred?.message || ''}
                        name={`personalityAlignments.${index}.leastPreferred`}
                      />
                    )}
                  />
                </div>
                {errors.personalityAlignments?.[index]?.mostPreferred && (
                  <small className="text-xs text-[#e74444] block mb-3">
                    {errors.personalityAlignments?.[index]?.mostPreferred?.message}
                  </small>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </BaseEditModal>
  );
};
